// Practice card (money-mode §1.4; right column, between the mode card and the money card): the
// third door on the home screen. Play (the play card) is classic FFA; this card is the free
// practice arena with the money room's rules and play money (no sign-in, no buy-in); the money
// card is real money.
//
// States (from app.ts `practice` flow + /api/info.practice, which the app passes in on every
// /api/info answer):
// - hidden: before the first /api/info answer, or the server has no practice arena (practice
//   off, or an older server without `practice`).
// - list (flow off): "Practice arena", a FREE tag, one line about it, and one button with the
//   live head count of the room you would join ("Practice · 3/30 playing") or "full".
// - joining: spinner text, "Joining the practice arena…".
// - room (ready): "You are in the practice arena", the live count, "Press Play: you start with
//   $0.90 of play money", and "Back to free play" (disabled during a life: cash out or die
//   first).
// - error: the reason ("The practice arena is closed right now.") and Back.
//
// `practiceCardView` is DOM-free (unit-tested). Consumer: app.ts.

import type { PracticeFlowState } from '../../app.js';
import type { UiClickSource } from '../../events/bus.js';
import type { ApiPracticeInfo } from '../../net/discovery.js';
import { h, type ReadonlySignal, show, type Unsubscribe } from '../dom.js';
import { t } from '../i18n/t.js';
import { entryValueText, formatStake } from './money.js';

export type PracticeCardMode = 'hidden' | 'list' | 'joining' | 'room' | 'error';

export interface PracticeCardInput {
  readonly flow: PracticeFlowState;
  /** The newest /api/info.practice, or null (absent / not loaded). */
  readonly info: ApiPracticeInfo | null;
  /** The money rake the play money mirrors (the "$0.90" line); absent = 10 %. */
  readonly rake?: number;
  /** Humans + bots in the joined practice room (ROOM section), or null. */
  readonly roomPlayers: number | null;
  /** A practice life is on screen (Back is disabled). */
  readonly lifeActive: boolean;
}

export interface PracticeCardView {
  readonly mode: PracticeCardMode;
  readonly blurb: string;
  /** The join button's label (list mode), e.g. "Practice · 3/30 playing". */
  readonly joinLabel: string;
  readonly canJoin: boolean;
  /** room / joining / error: the status lines. */
  readonly status: readonly string[];
  readonly canLeave: boolean;
}

/** What the practice card shows. */
export function practiceCardView(m: PracticeCardInput): PracticeCardView {
  const info = m.info;
  const stake = formatStake(info?.stakeUsd ?? 1);
  const blurb = t('practice.blurb', { stake });
  const step = m.flow.step;
  const none = { blurb, joinLabel: '', canJoin: false, status: [], canLeave: false };
  if (step === 'joining') {
    return { ...none, mode: 'joining', status: [t('practice.joining')] };
  }
  if (step === 'ready') {
    const status = [t('practice.inRoom')];
    if (m.roomPlayers !== null) status.push(t('money.roomPlayers', { n: m.roomPlayers }));
    status.push(
      t('practice.startWith', { value: entryValueText(info?.stakeUsd ?? 1, m.rake ?? 0.1) }),
    );
    return { ...none, mode: 'room', status, canLeave: !m.lifeActive };
  }
  if (step === 'error') {
    return { ...none, mode: 'error', status: [t('practice.closed')], canLeave: true };
  }
  if (info === null || !info.enabled) return { ...none, mode: 'hidden' };
  const players = info.full
    ? t('money.stakeFull')
    : t('money.stakePlayers', { n: info.joinHumans, max: info.maxPlayers });
  return {
    ...none,
    mode: 'list',
    joinLabel: t('practice.join', { players }),
    canJoin: info.available && !info.full,
  };
}

export interface PracticeCardDeps {
  readonly flow: ReadonlySignal<PracticeFlowState>;
  /** Join the practice arena (app.openPractice). */
  open(): void;
  /** Back to free play (app.leavePractice). */
  leave(): void;
  lifeActive(): boolean;
  roomPlayers(): number | null;
  rake(): number;
  playClick?(source: UiClickSource): void;
}

export interface PracticeCard {
  readonly el: HTMLElement;
  /** The newest /api/info.practice (null: none or not loaded). Re-renders. */
  setInfo(info: ApiPracticeInfo | null): void;
  /** Re-render (life start / end, room counts). */
  refresh(): void;
  dispose(): void;
}

export function createPracticeCard(deps: PracticeCardDeps): PracticeCard {
  let info: ApiPracticeInfo | null = null;
  const blurb = h('p', { class: 'practice-blurb' });
  const status = h('div', { class: 'practice-status', role: 'status' });
  const join = h(
    'button',
    {
      type: 'button',
      class: 'btn practice-join',
      onClick: () => {
        deps.playClick?.('other');
        deps.open();
      },
    },
    '',
  );
  const leave = h(
    'button',
    {
      type: 'button',
      class: 'btn practice-leave',
      onClick: () => {
        deps.playClick?.('other');
        deps.leave();
      },
    },
    t('money.leave'),
  );
  const el = h(
    'section',
    { class: 'card practice-card', hidden: true, 'data-mode': 'hidden' },
    h(
      'h2',
      { class: 'card-title practice-title' },
      t('practice.title'),
      h('span', { class: 'money-tag practice-tag' }, t('practice.free')),
    ),
    blurb,
    status,
    join,
    leave,
  );

  const render = (): void => {
    const v = practiceCardView({
      flow: deps.flow.get(),
      info,
      rake: deps.rake(),
      roomPlayers: deps.roomPlayers(),
      lifeActive: deps.lifeActive(),
    });
    el.dataset['mode'] = v.mode;
    show(el, v.mode !== 'hidden');
    blurb.textContent = v.blurb;
    show(blurb, v.mode === 'list');
    status.replaceChildren(...v.status.map((line) => h('p', null, line)));
    show(status, v.status.length > 0);
    join.textContent = v.joinLabel;
    (join as HTMLButtonElement).disabled = !v.canJoin;
    show(join, v.mode === 'list');
    (leave as HTMLButtonElement).disabled = !v.canLeave;
    show(leave, v.mode === 'room' || v.mode === 'error');
  };

  const offs: Unsubscribe[] = [deps.flow.subscribe(() => render())];
  render();
  return {
    el,
    setInfo(next: ApiPracticeInfo | null): void {
      info = next;
      render();
    },
    refresh: render,
    dispose(): void {
      for (const off of offs) off();
    },
  };
}
